import { useEffect, useMemo, useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { getCustomerHealth, getSalesAnalytics } from "../../distflowAPI/salesAnalyticsApi";
import useAppSelector from "../../hooks/useAppSelector";
import AnalyticsHeader from "./AnalyticsHeader";
import Summary from "./Summary";
import CustomerList from "./CustomerList";
import CustomerPanel from "./CustomerPanel";
import ProductOpportunities from "./ProductOpportunities";
import SalesmanScorecard from "./SalesmanScorecard";
import HolidaySeason from "./HolidaySeason";
import { addDrillFilter, canDrill, customerLevelFilters, dimensionOfKey } from "./drillDown";
import { PANEL_KEYS, buildRequest, filterKeys, parseState, writeStateToAddress } from "./urlState";
import useSideReport from "./useSideReport";
import "./SalesAnalytics.css";

function SalesAnalytics() {
  const isStaff = useAppSelector((state) => state.user.authUser?.is_staff === true);
  const [view, setView] = useState(() => parseState(window.location.search));
  const [requestError, setRequestError] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [retry, setRetry] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([view.tab]));

  // Only the parts that change the numbers trigger a reload; tabs, lists and panels do not.
  const requestKey = JSON.stringify([view.range, view.from, view.to, view.compare, view.cfrom, view.cto, view.territory]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applied = useMemo(() => buildRequest(view), [requestKey]);
  const filtersKey = filterKeys(view).join("\n");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filters = useMemo(() => (filtersKey ? filtersKey.split("\n") : []), [filtersKey]);
  const request = useMemo(() => ({ ...applied, filters }), [applied, filters]);
  // The Customers tab shows how many customers need a call. Same request as the tabs make, so it is shared.
  const asOf = report?.date_range?.end_date;
  const healthParams = useMemo(() => ({ as_of: asOf, territory_id: applied.territory_id, filters: customerLevelFilters(filters) }),
    [asOf, applied.territory_id, filters]);
  const health = useSideReport(getCustomerHealth, healthParams, isStaff && !loading && Boolean(asOf));

  function update(patch) {
    setView((current) => {
      const next = { ...current, ...patch };
      // Moving to another tab closes any open details panel, unless the move opens one.
      if (patch.tab && patch.tab !== current.tab) {
        PANEL_KEYS.filter((key) => !(key in patch)).forEach((key) => { next[key] = null; });
      }
      writeStateToAddress(next);
      return next;
    });
    if (patch.tab) setVisitedTabs((current) => (current.has(patch.tab) ? current : new Set([...current, patch.tab])));
  }

  // A salesman sets the Salesman filter; any other name focuses the page on it.
  function drillInto(row) {
    if (!canDrill(row)) return;
    if (dimensionOfKey(row.key) === "salesman") {
      const id = row.key.split(":")[1];
      update({ salesman: id === "unassigned" ? "unassigned" : Number(id), salesmanName: row.name || "Office / Unassigned" });
      return;
    }
    update({ focus: addDrillFilter(view.focus, row) });
  }

  // From a details panel: focus every tab on this customer or product, starting with Summary.
  function focusOn(row) {
    update({ focus: addDrillFilter(view.focus, row), tab: "summary" });
  }
  function focusSalesman(row) {
    update({ salesman: row.id ?? "unassigned", salesmanName: row.name, tab: "summary" });
  }
  const openCustomer = (key) => update({ tab: "customers", customer: key });
  const openProduct = (key) => update({ tab: "products", product: key });

  useEffect(() => {
    if (!isStaff) return undefined;
    const controller = new AbortController();
    // Keep the previous report on screen (dimmed) while the next one loads.
    setLoading(true);
    setRequestError("");
    getSalesAnalytics(request, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setReport(data);
        setLoading(false);
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.code === "ERR_CANCELED") return;
        setReport(null);
        setLoading(false);
        setRequestError(error?.response?.status === 403
          ? "Only admins can view sales analytics."
          : "Sales analytics could not be loaded. Please try again.");
      });
    return () => controller.abort();
  }, [request, isStaff, retry]);

  if (!isStaff) return <div className="sales-analytics-page"><div className="sales-analytics-access">
    <h1>Sales analytics</h1><p>Only admins can view sales analytics.</p></div></div>;

  const summary = report?.summary;
  const empty = report && Number(summary?.total_sales || 0) === 0 &&
    Number(summary?.previous_total_sales || 0) === 0 &&
    !Object.values(report.breakdowns || {}).some((list) => list?.length);
  const tab = view.tab;
  const scope = [view.territoryName || "All territories", view.salesmanName || "All salesmen"].join(", ");
  const customerPanel = (mode) => <CustomerPanel key={view.customer} customerKey={view.customer} asOf={report?.date_range?.end_date} mode={mode}
    onClose={() => update({ customer: null })} onFocus={focusOn} />;
  const panel = (key) => ({ role: "tabpanel", id: `sales-analytics-panel-${key}`, "aria-labelledby": `sales-analytics-tab-${key}`, className: "sales-analytics-panel", hidden: tab !== key });

  return <div className="sales-analytics-page">
    <AnalyticsHeader state={view} report={report} onChange={update} onInfo={() => setInfoOpen(true)}
      counts={{ customers: health.data?.summary?.flagged || 0 }} />
    {requestError && <Alert severity="error" className="sales-analytics-request-alert" action={<Button color="inherit" size="small" onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>{requestError}</Alert>}
    {loading && !report && <div className="sales-analytics-loading" role="status"><CircularProgress size={28} /><span>Loading sales analytics…</span></div>}
    {report && <div className={`sales-analytics-content${loading ? " sales-analytics-content--refreshing" : ""}`} aria-busy={loading}>
      {visitedTabs.has("summary") && <div {...panel("summary")}>
        {empty
          ? <section className="sales-analytics-empty"><h2>No sales for this selection</h2><p>Choose other dates, a territory or a salesman.</p></section>
          : <Summary report={report} view={view} applied={applied} filters={filters} ready={!loading} onChange={update} onDrill={drillInto}
            onOpenCustomer={openCustomer} onOpenProduct={openProduct} onOpenSalesman={(key) => update({ tab: "salesmen", rep: key })} />}
      </div>}
      {visitedTabs.has("customers") && <div {...panel("customers")}>
        <CustomerList report={report} asOf={report.date_range?.end_date} territoryId={applied.territory_id} filters={filters}
          scope={scope}
          list={view.list} onListChange={(list) => update({ list })}
          openKey={tab === "customers" ? view.customer : null} onOpen={(key) => update({ customer: key })}
          renderPanel={customerPanel} />
      </div>}
      {visitedTabs.has("products") && <div {...panel("products")}>
        <ProductOpportunities request={applied} filters={filters} openKey={tab === "products" ? view.product : null}
          onOpen={(key) => update({ product: key })} onClose={() => update({ product: null })} onOpenCustomer={openCustomer} onFocus={focusOn} />
      </div>}
      {visitedTabs.has("salesmen") && <div {...panel("salesmen")}>
        <SalesmanScorecard request={applied} scope={scope} openKey={tab === "salesmen" ? view.rep : null}
          onOpen={(key) => update({ rep: key })} onClose={() => update({ rep: null })}
          onOpenCustomer={openCustomer} onOpenProduct={openProduct} onFocusSalesman={focusSalesman} />
      </div>}
      {visitedTabs.has("seasons") && <div {...panel("seasons")}>
        <HolidaySeason territoryId={applied.territory_id} filters={filters} onSelect={drillInto}
          openKey={tab === "seasons" ? view.customer : null} onOpenCustomer={(key) => update({ customer: key })} onOpenProduct={openProduct}
          renderCustomerPanel={customerPanel} />
      </div>}
    </div>}
    <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} aria-labelledby="sales-analytics-info-title">
      <DialogTitle id="sales-analytics-info-title">How these numbers are calculated</DialogTitle>
      <DialogContent>
        <p>Sales use order totals on fulfillment dates. Canceled orders and voided invoices are excluded; payment dates do not change sales. Returns and signed corrections can reduce a breakdown row.</p>
        <p>Route, vendor, category and product use delivered items. Fees, unknown routes and differences from the stored order total show under the breakdown as fees and adjustments, so every breakdown adds up to total sales.</p>
        <p>An open day is a day with at least one delivered order in the selected territory, whatever else you have chosen. “Per open day” divides sales by those days, so a week with Yom Tov compares fairly with a full week.</p>
        <p>Gross profit is item sales less the recorded cost of what was delivered: the line’s cost, or else the product’s cost (per piece when sold by the piece). Products with no cost are left out, returns reduce sales but do not give back cost, and fees are not included. Margin is gross profit divided by the sales of products with a cost.</p>
        <p>Reporting dates use New York time. Comparisons move back by whole weeks, so Monday is compared with Monday and Shabbat with Shabbat; “same period last Hebrew year” uses the nearest same weekday. On Sales by day, Shabbos and Yom Tov are shaded, Hebrew dates run under the dates, and a line under the dates marks the days the comparison dates were closed for Yom Tov.</p>
        <p>Choosing a salesman shows only their orders and customers on every tab except Salesmen, which always shows the whole team. Clicking another name focuses the page on it: a customer or account type keeps full order totals, while a route, vendor, category or product counts only those items, so fees and adjustments are left out.</p>
        <p>Units are delivered quantities minus returns, in each product’s own selling unit. New customers placed their first ever order in the selected dates; “back again” customers had ordered before but not in the comparison dates; lost customers ordered in the comparison dates but not in the selected ones.</p>
        <p>“Needs a call” compares each regular customer’s last 4 weeks, up to the end of the selected dates, with their 12 weeks before: late against their own usual gap, buying elsewhere when they stop a category or staple they bought most weeks, and spending, ordering or order size well below usual. “At stake a week” is the usual weekly spend that has stopped or dropped. A customer’s panel suggests products bought by at least 30% of other customers of the same type in the last 12 weeks.</p>
        <p>The salesman panel’s call list and products to push use the same rules, narrowed to that salesman’s customers.</p>
        <p>Dates, filters, the tab, the list and any open customer, salesman or product are kept in the page address, so “Copy link” shares this view.</p>
      </DialogContent>
      <DialogActions><Button onClick={() => setInfoOpen(false)}>Close</Button></DialogActions>
    </Dialog>
  </div>;
}
export default SalesAnalytics;
